import {
  AREA_MOCK_RATIOS,
  DEFAULT_LEADER,
  DEFAULT_MAINTENANCE_UNIT,
  DEFAULT_TEAM_INFO,
  DEFAULT_WORKLOG,
  LENGTH_MOCK_RATIOS,
  TEAM_STATUSES,
} from './constants';
import { formatDateTime, parseDateToMs, uniqueFilled } from './helpers';
import type { LayerGroupAggregate, PolygonFeature, TeamCard, TimelineItem, ZoneMetrics } from './types';

export function buildLayerAggregates(features: PolygonFeature[]): LayerGroupAggregate[] {
  const grouped = new Map<string, LayerGroupAggregate>();

  for (const feature of features) {
    const { Layer, Shape_Area, Shape_Leng, RoadLength, TeamLeader, TeamInform, WorkLog, SurveyDate, Maintenanc } =
      feature.properties;

    const key = Layer || '未命名地块';
    const existing = grouped.get(key);

    if (existing) {
      existing.features.push(feature);
      existing.areaM2Total += Shape_Area;
      existing.shapeLengthMTotal += Shape_Leng;
      existing.roadLengthMTotal += RoadLength;
      existing.leaders = uniqueFilled([...existing.leaders, TeamLeader]);
      existing.teamInfos = uniqueFilled([...existing.teamInfos, TeamInform]);
      existing.workLogs = uniqueFilled([...existing.workLogs, WorkLog]);
      existing.surveyDates = uniqueFilled([...existing.surveyDates, SurveyDate]);
      existing.maintenanceUnits = uniqueFilled([...existing.maintenanceUnits, Maintenanc]);
      continue;
    }

    grouped.set(key, {
      layerName: key,
      features: [feature],
      areaM2Total: Shape_Area,
      shapeLengthMTotal: Shape_Leng,
      roadLengthMTotal: RoadLength,
      leaders: TeamLeader ? [TeamLeader] : [],
      teamInfos: TeamInform ? [TeamInform] : [],
      workLogs: WorkLog ? [WorkLog] : [],
      surveyDates: SurveyDate ? [SurveyDate] : [],
      maintenanceUnits: Maintenanc ? [Maintenanc] : [],
    });
  }

  return [...grouped.values()].sort((a, b) => b.areaM2Total - a.areaM2Total);
}

export function buildZoneMetrics(realZoneAreaM2: number, realZoneShapeLengthKm: number, surveyDate: string): ZoneMetrics[] {
  const zones: ZoneMetrics[] = [];

  zones.push({
    zoneId: 'zone-8',
    zoneName: '区域8',
    displayName: '区域8',
    order: 0,
    areaM2: realZoneAreaM2,
    shapeLengthKm: realZoneShapeLengthKm,
    isMock: false,
    lastSurveyDate: surveyDate,
  });

  AREA_MOCK_RATIOS.forEach((ratio, index) => {
    zones.push({
      zoneId: `zone-${index + 1}`,
      zoneName: `区域${index + 1}`,
      displayName: `模拟${index + 1}`,
      order: index + 1,
      areaM2: realZoneAreaM2 * ratio,
      shapeLengthKm: realZoneShapeLengthKm * LENGTH_MOCK_RATIOS[index],
      isMock: true,
      lastSurveyDate: surveyDate,
    });
  });

  return zones.sort((a, b) => a.order - b.order);
}

export function buildTeams(layerAggregates: LayerGroupAggregate[]): TeamCard[] {
  const cards = layerAggregates.map((aggregate, index) => {
    const maintenanceUnit = aggregate.maintenanceUnits[0] ?? DEFAULT_MAINTENANCE_UNIT;
    const teamInfo = aggregate.teamInfos[0] ?? DEFAULT_TEAM_INFO;
    const leader = aggregate.leaders[0] ?? DEFAULT_LEADER;

    return {
      maintenanceUnit,
      teamInfo,
      leader,
      featureCount: aggregate.features.length,
      areaM2: aggregate.areaM2Total,
      shapeLengthKm: aggregate.shapeLengthMTotal / 1000,
      roadLengthKm: aggregate.roadLengthMTotal / 1000,
      status: TEAM_STATUSES[index % TEAM_STATUSES.length],
    };
  });

  return cards.sort((a, b) => b.areaM2 - a.areaM2);
}

export function buildTimeline(layerAggregates: LayerGroupAggregate[], regionName: string): TimelineItem[] {
  const items: TimelineItem[] = [];
  let slot = 0;

  for (const aggregate of layerAggregates) {
    const baseDate = aggregate.surveyDates[0] ?? '';
    const baseTs = parseDateToMs(baseDate);
    const workLogs = aggregate.workLogs.length ? aggregate.workLogs : [DEFAULT_WORKLOG];

    for (const log of workLogs) {
      const timestamp = baseTs + ((17 - slot) * 41 + 15) * 60_000;
      items.push({
        id: `${aggregate.layerName}-${slot}`,
        timestamp,
        timeText: formatDateTime(timestamp),
        leader: aggregate.leaders[0] ?? DEFAULT_LEADER,
        zoneName: regionName,
        maintenanceUnit: aggregate.maintenanceUnits[0] ?? DEFAULT_MAINTENANCE_UNIT,
        description: `${aggregate.layerName}：${log}`,
      });
      slot += 1;
    }
  }

  while (items.length < 8 && layerAggregates.length > 0) {
    const source = layerAggregates[items.length % layerAggregates.length];
    const timestamp = Date.now() - items.length * 48 * 60_000;
    items.push({
      id: `mock-${items.length}`,
      timestamp,
      timeText: formatDateTime(timestamp),
      leader: source.leaders[0] ?? DEFAULT_LEADER,
      zoneName: regionName,
      maintenanceUnit: source.maintenanceUnits[0] ?? DEFAULT_MAINTENANCE_UNIT,
      description: `${source.layerName}：${DEFAULT_WORKLOG}`,
    });
  }

  return items.sort((a, b) => b.timestamp - a.timestamp).slice(0, 12);
}
